// @ts-nocheck
/**
 * Expiry column cell for the users list:
 *   • bold relative countdown ("in 3 days", "today", "expired 4d ago")
 *   • coloured tone by proximity (rose ≤ 7d + expired, amber ≤ 30d,
 *     muted foreground otherwise)
 *   • absolute date on the second line so ops still sees the exact day
 *
 * If the input date is missing / invalid, renders an em-dash only.
 */
import { cn } from '@/lib/utils/cn';

interface Props {
  date: string | number | Date | null | undefined;
  className?: string;
}

function startOfDay(d: Date): Date {
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatRelative(days: number): string {
  if (days < 0) {
    const abs = Math.abs(days);
    if (abs === 1) return 'expired 1d ago';
    if (abs < 7) return `expired ${abs}d ago`;
    if (abs < 30) return `expired ${Math.round(abs / 7)}w ago`;
    if (abs < 365) return `expired ${Math.round(abs / 30)}mo ago`;
    return `expired ${Math.round(abs / 365)}y ago`;
  }
  if (days === 0) return 'expires today';
  if (days === 1) return 'in 1 day';
  if (days < 30) return `in ${days} days`;
  if (days < 365) return `in ${Math.round(days / 30)} months`;
  return `in ${Math.round(days / 365)} years`;
}

export function UserExpiryCell({ date, className }: Props) {
  if (!date) return <span className="text-muted-foreground">—</span>;

  const raw = new Date(date);
  if (Number.isNaN(raw.getTime())) {
    return <span className="text-muted-foreground">—</span>;
  }
  const target = startOfDay(new Date(raw));
  const today = startOfDay(new Date());
  const diffMs = target.getTime() - today.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));

  const tone =
    days < 0
      ? 'text-rose-700 dark:text-rose-300'
      : days <= 7
      ? 'text-rose-600 dark:text-rose-300'
      : days <= 30
      ? 'text-amber-600 dark:text-amber-300'
      : 'text-emerald-700 dark:text-emerald-300';

  const dateStr = raw.toLocaleDateString('en-GB');

  return (
    <div className={cn('leading-tight', className)}>
      <div className={cn('text-[13px] font-semibold', tone)}>{formatRelative(days)}</div>
      <div className="text-[11px] text-muted-foreground">{dateStr}</div>
    </div>
  );
}
